/**
 * Maps each route to its renderer and page behaviour. Route metadata (titles, descriptions) lives in
 * config/routes.ts so the Node build can read it without importing page code.
 */
import { findSitePost, sitePostMeta } from '../../content/site-posts';
import { FAQ_ALL, type FaqKey } from '../../content/faq';
import { findRoute, INSIGHT_ARTICLE_PREFIX, NOT_FOUND_META, type RouteMeta, type RoutePath } from '../../config/routes';
import { pageArticle } from '../../pages/article';
import { pageCalculator } from '../../pages/calculator';
import { pageCareers } from '../../pages/careers';
import { pageCompany } from '../../pages/company';
import { pageContact } from '../../pages/contact';
import { pageCustomers } from '../../pages/customers';
import { pageDemo } from '../../pages/demo';
import { pageHome } from '../../pages/home';
import { pageInsights } from '../../pages/insights';
import { pageLegal } from '../../pages/legal/legal-page';
import { page404 } from '../../pages/not-found';
import { pagePlatform } from '../../pages/platform';
import { pageQuebec } from '../../pages/quebec';
import { pageSecurity } from '../../pages/security';
import { pageSolutions } from '../../pages/solutions';
import { initCalculator } from '../calculator/calculator';
import type { SitePost } from '../insights/insight-types';
import { initInsights } from '../insights/insights-tabs';
import { initSubnav } from '../ui/subnav';

export type PageRenderer = (query: URLSearchParams) => string;
export type PageInitializer = (root: HTMLElement) => void;

interface PageDefinition {
  readonly render: PageRenderer;
  readonly init?: PageInitializer;
  /** FAQ entries published as FAQPage structured data. */
  readonly faq?: readonly FaqKey[];
}

export interface ResolvedPage extends PageDefinition {
  readonly meta: RouteMeta;
  readonly article?: SitePost;
}

const PAGES: Readonly<Record<RoutePath, PageDefinition>> = {
  '/': { render: pageHome },
  '/platform': { render: pagePlatform },
  '/solutions': { render: pageSolutions, init: initSubnav, faq: FAQ_ALL },
  '/customers': { render: pageCustomers },
  '/security': { render: pageSecurity },
  '/insights': { render: pageInsights, init: initInsights },
  '/company': { render: pageCompany },
  '/careers': { render: pageCareers },
  '/contact': { render: pageContact },
  '/demo': { render: pageDemo },
  '/calculator': { render: pageCalculator, init: initCalculator },
  '/quebec': { render: pageQuebec },
  '/privacy': { render: () => pageLegal('privacy') },
  '/terms': { render: () => pageLegal('terms') },
};

export const NOT_FOUND_PAGE: ResolvedPage = { meta: NOT_FOUND_META, render: page404 };

function resolveArticle(path: string): ResolvedPage | null {
  if (!path.startsWith(INSIGHT_ARTICLE_PREFIX)) return null;
  const post = findSitePost(path.slice(INSIGHT_ARTICLE_PREFIX.length));
  if (!post) return null;
  return {
    meta: sitePostMeta(post),
    render: () => pageArticle(post),
    article: post,
  };
}

/** The page for a path, or null when nothing matches (the caller shows the 404 page). */
export function resolvePage(path: string): ResolvedPage | null {
  const meta = findRoute(path);
  if (meta) return { meta, ...PAGES[meta.path] };
  return resolveArticle(path);
}
